import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import config from '../../config';
import { logError } from '../log';
import { isCordova } from './deviceDetection';

// Stripe's success_url / cancel_url validator rejects the Cordova `app://`
// scheme, and even when it didn't, we could not detect the return navigation
// inside an InAppBrowser reliably. Anchor all returns on the canonical HTTPS
// origin and intercept them via a loadstart listener in Cordova.
//
// We intentionally use window.location.pathname here rather than the
// react-router location, because on Cordova the app loads from /index.html
// with hash-based routing, and Stripe will redirect the InAppBrowser to
// whatever pathname we hand it — the react-router path only exists after the
// in-app webview resolves the hash. Using window.location.pathname keeps the
// URL stitched together with the same value in both the duck (which builds
// the success_url) and the component (which builds the prefix matcher).
export const computeStripeReturnUrl = () => {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
  if (isCordova()) {
    return `${config.canonicalRootURL}${pathname}`;
  }
  return `${window.location.origin}${pathname}`;
};

export const openStripeHosted = (stripeUrl, options = {}) => {
  const { returnUrlPrefix, onReturn, onCancel } = options;

  if (!isCordova() || !window?.cordova?.InAppBrowser) {
    window.location.href = stripeUrl;
    return;
  }

  const ref = window.cordova.InAppBrowser.open(
    stripeUrl,
    '_blank',
    'location=yes,hidenavigationbuttons=no,clearsessioncache=yes,clearcache=yes',
  );

  if (!ref || typeof ref.addEventListener !== 'function') {
    window.location.href = stripeUrl;
    return;
  }

  let settled = false;

  const handleLoadStart = (event) => {
    const url = event?.url;
    if (!url || !returnUrlPrefix || !url.startsWith(returnUrlPrefix)) return;
    settled = true;
    try {
      const parsed = new URL(url);
      const params = Object.fromEntries(parsed.searchParams.entries());
      ref.close();
      if (onReturn) onReturn(params);
    } catch (e) {
      logError(e, 'stripe-external-checkout-return-parse');
    }
  };

  const handleExit = () => {
    ref.removeEventListener('loadstart', handleLoadStart);
    ref.removeEventListener('exit', handleExit);
    if (!settled && onCancel) onCancel();
  };

  ref.addEventListener('loadstart', handleLoadStart);
  ref.addEventListener('exit', handleExit);
};

/**
 * Opens a Stripe-hosted checkout and, in the Cordova app, replays Stripe's
 * return params into the router search so the page's fulfil effect runs
 * exactly as it does after a web redirect.
 *
 * @returns {(url: string, options?: { onCancel?: () => void }) => void}
 */
export const useOpenCheckoutReturningHere = () => {
  const navigate = useNavigate();

  return useCallback(
    (url, { onCancel } = {}) => {
      openStripeHosted(url, {
        returnUrlPrefix: computeStripeReturnUrl(),
        onReturn: (params) => {
          const search = new URLSearchParams(params).toString();
          navigate({ search: `?${search}` }, { replace: true });
        },
        onCancel,
      });
    },
    [navigate]
  );
};
